// Plan selection component for onboarding and subscription management
import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Check, Loader2 } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import { supabase } from '@/lib/supabase'
import { formatCurrency } from '@/lib/utils'

interface PlanSelectorProps {
  onComplete?: () => void
}

interface Plan {
  id: string
  product_name: string
  product_description: string
  price: number
  currency: string
  interval: 'month' | 'year'
  features: { name: string }[]
}

interface GroupedPlan {
  name: string
  description: string
  monthlyPrice: number
  yearlyPrice: number
  monthlyPlanId: string
  yearlyPlanId: string
  isEnterprise?: boolean
  features: { name: string }[]
}

// Group plans by their base name (e.g., "basic", "professional", "enterprise")
const groupPlans = (plans: Plan[] = []): GroupedPlan[] => {
  const grouped: { [key: string]: GroupedPlan } = {}

  plans.forEach(plan => {
    const baseName = plan.product_name.toLowerCase().split('_')[0]
    const isYearly = plan.interval === 'year'

    if (!grouped[baseName]) {
      grouped[baseName] = {
        name: baseName.charAt(0).toUpperCase() + baseName.slice(1),
        description: plan.product_description || `${baseName.charAt(0).toUpperCase() + baseName.slice(1)} plan with essential features`,
        monthlyPrice: 0,
        yearlyPrice: 0,
        monthlyPlanId: '',
        yearlyPlanId: '',
        isEnterprise: baseName === 'enterprise',
        features: []
      }
    }

    if (isYearly) {
      grouped[baseName].yearlyPrice = plan.price / 100 / 12 // Convert to monthly equivalent
      grouped[baseName].yearlyPlanId = plan.id
      if (!grouped[baseName].features.length) {
        grouped[baseName].features = plan.features || []
      }
    } else {
      grouped[baseName].monthlyPrice = plan.price / 100
      grouped[baseName].monthlyPlanId = plan.id
      if (!grouped[baseName].features.length) {
        grouped[baseName].features = plan.features || []
      }
    }
  })

  return Object.values(grouped).sort((a, b) => a.monthlyPrice - b.monthlyPrice)
}

export function PlanSelector({ onComplete }: PlanSelectorProps) {
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true)
        const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }
        
        if (!account?.account_id) {
          throw new Error('No account found')
        }

        const session = await supabase.auth.getSession()
        if (!session.data.session) {
          throw new Error('No session found')
        }

        const workerUrl = import.meta.env.VITE_WORKERS_URL
        const response = await fetch(`${workerUrl}/billing-functions/get_plans`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`
          },
          body: JSON.stringify({ account_id: account.account_id })
        })

        if (!response.ok) {
          throw new Error('Failed to fetch plans')
        }

        const data = await response.json()
        // Transform the plan details into the format expected by groupPlans
        const transformedPlans = data.map((plan: any) => ({
          id: plan.id,
          product_name: plan.product_name,
          product_description: plan.product_description,
          price: plan.price,
          currency: plan.currency,
          interval: plan.interval === 'month' ? 'month' : 'year',
          features: Array.isArray(plan.features) ? plan.features.map((feature: any) => {
            if (typeof feature === 'string') {
              return { name: feature }
            }
            return feature
          }) : []
        }))
        setPlans(transformedPlans)
      } catch (error) {
        console.error('Error fetching plans:', error)
        toast({
          title: "Error",
          description: "Failed to load subscription plans",
          variant: "destructive"
        })
      } finally {
        setLoading(false)
      }
    }

    fetchPlans()
  }, [])

  const groupedPlans = groupPlans(plans)

  const handleSelectPlan = async (planId: string) => {
    try {
      setSelectedPlanId(planId)
      setCheckoutLoading(true)
      const session = await supabase.auth.getSession()
      if (!session.data.session) {
        throw new Error('No session found')
      }

      const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }
      if (!account?.account_id) {
        throw new Error('No account found')
      }

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/billing-functions/create_checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          account_id: account.account_id,
          plan_id: planId,
          billing_interval: billingInterval,
          success_url: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/onboarding?step=plan`
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create checkout session')
      }

      const { session_id, url } = await response.json()
      console.log('session_id', session_id)
      if (url) {
        window.location.href = url
      }

      if (onComplete) {
        onComplete()
      }
    } catch (error) {
      console.error('Error selecting plan:', error)
      toast({
        title: "Error",
        description: "Failed to process plan selection",
        variant: "destructive"
      })
    } finally {
      setCheckoutLoading(false)
      setSelectedPlanId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold">Choose Your Plan</h2>
        <p className="text-gray-600 mt-2">
          Select the plan that best fits your needs. All plans include a 14-day free trial.
        </p>
      </div>

      <div className="flex justify-center gap-2 mb-8">
        <Button
          variant={billingInterval === 'monthly' ? 'default' : 'outline'}
          onClick={() => setBillingInterval('monthly')}
        >
          Monthly
        </Button>
        <Button
          variant={billingInterval === 'yearly' ? 'default' : 'outline'}
          onClick={() => setBillingInterval('yearly')}
        >
          Yearly
          <span className="ml-2 text-xs bg-primary-foreground text-primary px-2 py-0.5 rounded-full">
            Save 20%
          </span>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
        {groupedPlans.map((plan) => (
          <Card
            key={plan.name}
            className={`p-6 relative ${
              plan.isEnterprise ? 'border-primary' : ''
            }`}
          >
            {plan.isEnterprise && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm">
                  Recommended
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">{plan.name}</h3>
              <p className="text-gray-600 text-sm mt-2">{plan.description}</p>
              <div className="mt-4">
                <span className="text-3xl font-bold">
                  {formatCurrency(billingInterval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice)}
                </span>
                <span className="text-gray-600">/month</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="text-green-500 h-5 w-5" />
                  <span className="text-sm">{feature.name}</span>
                </li>
              ))}
            </ul>

            <Button
              className="w-full"
              variant={plan.isEnterprise ? 'default' : 'outline'}
              onClick={() => handleSelectPlan(billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId)}
              disabled={checkoutLoading || selectedPlanId === (billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId)}
            >
              {checkoutLoading && selectedPlanId === (billingInterval === 'monthly' ? plan.monthlyPlanId : plan.yearlyPlanId) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : plan.isEnterprise ? 'Contact Sales' : 'Select Plan'}
            </Button>
          </Card>
        ))}
      </div>

      <div className="text-center text-sm text-gray-600 mt-8">
        <p>
          All plans include a 14-day free trial.
          <br />
          Questions? Contact our sales team.
        </p>
      </div>
    </div>
  )
}